import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setForgotLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (resetError) {
        setError('Une erreur est survenue : ' + resetError.message);
      } else {
        setSuccess('Email de réinitialisation envoyé ! Vérifiez votre boîte mail.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        setError(
          signInError.message === 'Invalid login credentials'
            ? 'Email ou mot de passe incorrect'
            : 'Une erreur est survenue'
        );
      } else {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-8"
      >
        {/* Logo */}
        <div className="text-center space-y-3">
          <img src="/logo-blanc.svg" alt="Consentink" className="h-12 mx-auto object-contain" />
          <p className="text-muted-foreground">Gestion professionnelle des consentements</p>
        </div>

        {/* Forgot password form */}
        {forgotMode ? (
          <form onSubmit={handleForgotPassword} className="card-elevated p-8 space-y-5">
            <h2 className="text-xl font-bold font-display text-center">
              Réinitialiser le mot de passe
            </h2>

            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                  placeholder="email@exemple.com"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 text-destructive text-sm text-center">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-xl p-3 text-[hsl(var(--success))] text-sm text-center">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={forgotLoading}
              className="touch-button-lg w-full bg-primary text-primary-foreground rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {forgotLoading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (
                'Envoyer le lien'
              )}
            </button>

            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => { setForgotMode(false); setError(''); setSuccess(''); }}
                className="text-primary font-semibold"
              >
                Retour à la connexion
              </button>
            </div>
          </form>
        ) : (
          /* Login form */
          <form onSubmit={handleSubmit} className="card-elevated p-8 space-y-5">
            <h2 className="text-xl font-bold font-display text-center">Connexion</h2>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                  placeholder="email@exemple.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between -mt-1">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 cursor-pointer"
                />
                Se souvenir de moi
              </label>
              <button
                type="button"
                onClick={() => { setForgotMode(true); setError(''); setSuccess(''); }}
                className="text-sm text-primary font-medium hover:underline"
              >
                Mot de passe oublié ?
              </button>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 text-destructive text-sm text-center">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-xl p-3 text-[hsl(var(--success))] text-sm text-center">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="touch-button-lg w-full bg-primary text-primary-foreground rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (
                'Se connecter'
              )}
            </button>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Pas encore de compte ?{' '}</span>
              <Link to="/auth/register" className="text-primary font-semibold">
                Créer un compte
              </Link>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default LoginPage;
